import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, RefreshCw, AlertTriangle, DollarSign, Download, Search, Check } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { IgnoreControl, fetchIgnoreReasons } from './ActualCost2Ignore';
import { linkBrokerInvoice } from '../services/brokerInvoiceLookup';

// Categorical palette for the stacked first-sale-savings charts (color-blind-safe-ish, high contrast).
const ANA_PALETTE = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5', '#0d9488', '#9333ea'];

/**
 * ActualCost2Dashboard — CHA-323 / CHA-325. Per shipment (broker job#), SYSTEM (ERP accrued) vs
 * ACTUAL (ACE duty+tariff split by HTS, ac2_freight_by_job freight), First-Sale-aware verdicts.
 * CHA-325: term codes, tariff/freight diffs, CSV export, missing-data view, date range.
 */

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);
const signed = (v) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}$${Math.abs(Math.round(Number(v))).toLocaleString()}`);
// % difference of declared (ACE) vs system FOB: (declared - fob) / fob. Positive = declared higher.
const pctDiff = (declared, fob) => (declared == null || !fob ? null : ((declared - fob) / fob) * 100);
const pctFmt = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);

const VERDICTS = {
  'ok-first-sale': { label: 'First sale ✓', cls: 'bg-green-100 text-green-800' },
  ok: { label: 'OK', cls: 'bg-green-100 text-green-800' },
  'weak-first-sale': { label: 'FS over system — check', cls: 'bg-red-100 text-red-800' },
  'review-variance': { label: 'Review', cls: 'bg-red-100 text-red-800' },
  'hts-unallocated': { label: 'HTS unallocated', cls: 'bg-orange-100 text-orange-800' },
  // CHA-355 — the entry's ACE actuals include sibling invoices with no received counterpart;
  // Δ columns are structurally meaningless until the siblings land (or the link spelling is fixed).
  'partial-receipts': { label: 'Partial receipts', cls: 'bg-amber-100 text-amber-800' },
  'no-actual': { label: 'No ACE yet', cls: 'bg-slate-100 text-slate-600' },
};

function VerdictBadge({ v }) {
  const meta = VERDICTS[v] || { label: v, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${meta.cls}`}>{meta.label}</span>;
}

function Card({ label, value, sub, tone }) {
  return (
    <div className={`rounded-lg border p-3 ${tone === 'warn' ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200 bg-white'}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-800">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

// CHA-337 — first-sale savings breakdown: a horizontal bar chart of saving-$ by group, next to a
// compact exact-figures table (ships, units, FOB, FOB disc%, effective D+T rate, saving-$).
const anaTip = (v, n) => [money(v), n];
function AnaBreakdown({ title, rows, keyLabel }) {
  const top = rows.slice(0, 12);
  const chartH = Math.max(120, top.length * 34 + 20);
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-sm font-semibold text-slate-700 mb-2">{title}</div>
      {!rows.length ? (
        <div className="text-sm text-slate-500">No data.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div style={{ width: '100%', height: chartH }}>
            <ResponsiveContainer>
              <BarChart data={top} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid horizontal={false} stroke="#eef2f7" />
                <XAxis type="number" tickFormatter={(v) => money(v)} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="key" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => anaTip(v, 'First-sale saving')} />
                <Bar dataKey="saving" fill="#16a34a" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                {[keyLabel, 'Ships', 'FOB', 'FOB disc%', 'D+T rate', 'FS saving'].map((h) => <th key={h} className="text-left font-medium px-3 py-1.5 whitespace-nowrap">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.key} className="hover:bg-slate-50">
                    <td className="px-3 py-1.5 text-xs font-medium">{r.key}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{r.ships.toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.fob)}</td>
                    <td className="px-3 py-1.5 tabular-nums">{r.discPct.toFixed(1)}%</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{(r.rate * 100).toFixed(1)}%</td>
                    <td className="px-3 py-1.5 tabular-nums font-medium text-green-700">{money(r.saving)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// CSV injection (CWE-1236): Excel and Sheets EXECUTE a cell that opens with = + - or @, so a value
// carrying one turns the export into a formula the moment the operator opens it. Prefix with a tab so
// the spreadsheet treats it as text; the visible value is unchanged. Every export on this page goes
// through here, so this covers the shipment, by-style and missing-job files alike (CodeRabbit, #322).
const csvCell = (v) => {
  const raw = v == null ? '' : String(v);
  // A genuine NEGATIVE NUMBER must stay a number: this file is full of variances (duty_diff,
  // freight_diff) the operator sorts and sums, and tab-prefixing them would silently turn every one
  // into Excel text. Only neutralise values that are not numeric.
  const numeric = raw !== '' && Number.isFinite(Number(raw));
  const s = (!numeric && /^[=+\-@\t\r]/.test(raw)) ? `\t${raw}` : raw;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportCsv(shipments) {
  const cols = ['job_number', 'commercial_invoices', 'terms', 'warehouses', 'lc_name', 'factory_name', 'division', 'last_received', 'units', 'first_sale', 'verdict', 'old_tariff_in_duty',
    'system_fob', 'declared_value_ace', 'fob_pct_diff',
    'system_duty', 'actual_duty', 'duty_diff', 'system_tariff', 'actual_tariff', 'tariff_diff',
    'system_duty_tariff', 'actual_duty_tariff', 'duty_tariff_diff', 'saving_pct',
    'system_freight', 'actual_freight', 'freight_diff'];
  const rows = shipments.map((s) => {
    const pd = pctDiff(s.actual.value, s.system.fob);
    return [
      s.job_number, (s.invoices || []).join(' | '), (s.terms || []).join(' '), (s.warehouses || []).join(' '),
      // CHA-422 — a broker job can span several invoices, so these are legitimately multi-valued; join
      // rather than take [0], which would silently report one LC for a two-LC shipment.
      (s.lc_names || []).join(' | '), (s.factory_names || []).join(' | '),
      (s.division_names || []).join(' | ') || (s.divisions || []).join(' '),
      s.last_recv || '',
      s.units, s.fs ? 'FS' : '', s.verdict, s.hasRollIn ? 'YES' : '',
      s.system.fob, s.actual.value, pd == null ? '' : pd.toFixed(2),
      s.system.duty, s.actual.duty, s.variance.duty, s.system.tariff, s.actual.tariff, s.variance.tariff,
      s.system.dutyTariff, s.actual.dutyTariff, s.variance.dutyTariff, s.savingPct == null ? '' : s.savingPct,
      s.system.freight, s.actual.freight, s.variance.freight,
    ];
  });
  download(`actual_cost_2_${new Date().toISOString().slice(0, 10)}.csv`, [cols, ...rows]);
}

function exportStyles(styles) {
  const cols = ['job_number', 'commercial_invoice', 'style', 'hts', 'terms', 'first_sale', 'units', 'verdict',
    'system_fob', 'system_duty', 'actual_duty', 'duty_diff', 'system_tariff', 'actual_tariff', 'tariff_diff',
    'system_freight', 'actual_freight', 'freight_diff', 'duty_unallocated', 'duty_partial', 'duty_approx'];
  const rows = styles.map((s) => [
    s.job_number, s.comm_inv, s.sty, s.hts, (s.terms || []).join(' '), s.fs ? 'FS' : '', s.units, s.verdict,
    s.system.fob, s.system.duty, s.actual.duty, s.variance.duty, s.system.tariff, s.actual.tariff, s.variance.tariff,
    s.system.freight, s.actual.freight, s.variance.freight, s.unallocatedDuty ? 'YES' : '', s.partialDuty ? 'YES' : '', s.approxDuty ? 'YES' : '',
  ]);
  download(`actual_cost_2_by_style_${new Date().toISOString().slice(0, 10)}.csv`, [cols, ...rows]);
}

function exportMissing(unlinked) {
  const cols = ['commercial_invoice', 'terms', 'warehouses', 'last_received', 'units', 'note'];
  const rows = unlinked.map((u) => [u.comm_inv, (u.terms || []).join(' '), (u.warehouses || []).join(' '), u.last_recv || '', u.units, 'no broker job# — please provide']);
  download(`ac2_missing_job_numbers_${new Date().toISOString().slice(0, 10)}.csv`, [cols, ...rows]);
}

function download(filename, matrix) {
  const csv = matrix.map((r) => r.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// CHA-350 — case-insensitive substring match of a search query against a row's identifier parts
// (strings or arrays of strings; null/undefined parts ignored). Exported for tests.
export function matchesSearch(query, parts) {
  const q = String(query || '').trim().toUpperCase();
  if (!q) return true;
  return parts.flat().filter(Boolean).join(' ').toUpperCase().includes(q);
}

export default function ActualCost2Dashboard({ tenantId }) {
  const [view, setView] = useState('shipment'); // 'shipment' | 'style' | 'analysis' (CHA-316 phase 2 / CHA-337)
  const [stackDim, setStackDim] = useState('division'); // Analysis monthly chart stack: 'division' | 'customer'
  const [coast, setCoast] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [styleData, setStyleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [showDQ, setShowDQ] = useState(false);   // data-quality panel (by-style)
  const [cleanOnly, setCleanOnly] = useState(false); // hide excluded styles from the table
  const [search, setSearch] = useState(''); // CHA-350 — client-side identifier search
  const [hidePartial, setHidePartial] = useState(false); // CHA-359 — operator toggle: drop partial-receipts rows
  const [resolved, setResolved] = useState({}); // comm_inv -> job# just saved (optimistic)
  const [jobInputs, setJobInputs] = useState({});
  const [savingInv, setSavingInv] = useState(null);
  // CHA-379 — ignore support: reasons from AC2 Settings + comm_inv -> reason just ignored (optimistic,
  // same pattern as `resolved`; the row leaves unlinkedInvoices server-side on the next Run).
  const [ignoreReasons, setIgnoreReasons] = useState(null);
  const [ignoredLocal, setIgnoredLocal] = useState({});
  useEffect(() => { fetchIgnoreReasons(tenantId).then(setIgnoreReasons); }, [tenantId]);
  const reqRef = useRef(0);

  // Save (or overwrite) an invoice→job# link through the broker lookup gateway, keyed server-side
  // on the canonical invoice slug so a typo'd/missing invoice can be fixed from the missing list.
  const saveLink = async (comm_inv) => {
    const jobNumber = (jobInputs[comm_inv] || '').trim().toUpperCase();
    if (!jobNumber) return;
    setSavingInv(comm_inv);
    try {
      await linkBrokerInvoice(tenantId, {
        commercialInvoice: comm_inv,
        jobNumber,
        source: 'dashboard',
      });
      setResolved((r) => ({ ...r, [comm_inv]: jobNumber }));
    } catch (e) {
      setError(`Save failed for ${comm_inv}: ${e.message}`);
    } finally {
      setSavingInv(null);
    }
  };

  const run = useCallback(async () => {
    // CHA-354 — the backend rejects one-sided ranges; while the operator is mid-entry (one date
    // set, the other not yet), just wait instead of flashing that error.
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    const reqId = ++reqRef.current;
    setLoading(true); setError(null);
    try {
      const fnName = view === 'style' ? 'getAc2StyleAllocation' : 'getAc2Reconciliation';
      const fn = httpsCallable(getFunctions(), fnName, { timeout: 300000 });
      const res = await fn({ coast: coast || null, startDate: startDate || null, endDate: endDate || null });
      if (reqId !== reqRef.current) return;
      if (view === 'style') setStyleData(res.data); else setData(res.data);
    } catch (e) {
      if (reqId === reqRef.current) setError(e.message);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [coast, startDate, endDate, view]);

  useEffect(() => { run(); }, [run]);

  const s = data?.summary;
  const shipments = data?.shipments || [];
  const unlinked = data?.unlinkedInvoices || [];
  const noActual = shipments.filter((x) => !x.hasActual);
  const posted = shipments.filter((x) => x.hasActual);
  // CHA-350 — search filters the TABLES/LISTS only; summary tiles and Analysis aggregates stay on
  // the full selection so the headline numbers don't shift under a lookup.
  const shipMatch = (r) => matchesSearch(search, [r.job_number, r.invoices, r.prod_nums, r.styles, r.container, r.warehouses, r.customers, r.divisions, r.lc_names, r.factory_names, r.division_names])
    && !(hidePartial && r.verdict === 'partial-receipts'); // CHA-359
  const shownPosted = posted.filter(shipMatch);
  const shownNoActual = noActual.filter(shipMatch);
  const shownUnlinked = unlinked.filter((u) => matchesSearch(search, [u.comm_inv, u.styles, u.prod_nums, u.warehouses]));
  const totalFob = posted.reduce((t, x) => t + (x.system.fob || 0), 0);
  const totalDeclared = posted.reduce((t, x) => t + (x.actual.value || 0), 0);
  const linkedInvoiceCount = shipments.reduce((t, x) => t + (x.invoiceCount || 0), 0);
  const totalInvoices = linkedInvoiceCount + unlinked.length;
  const missingCount = unlinked.length + noActual.reduce((t, x) => t + (x.invoiceCount || 0), 0);

  // Analysis tab (CHA-337) — "how much are we saving due to first sale?" The saving is NOT system−actual
  // (that conflates first sale with rate/roll-in/accrual gaps). First sale lowers the DECLARED value, so
  // the true saving is the FOB reduction × the applicable duty+tariff rate: (fob − declared) × (sysDT/fob).
  //   e.g. FOB $100 → first-sale $90, rate 10%  ⇒  saving = $10 × 10% = $1.
  // Each shipment is attributed to its primary customer/division and bucketed by received month.
  const fsRows = posted.map((sh) => {
    const fob = sh.system.fob || 0;
    const declared = sh.actual.value || 0;
    const dt = sh.system.dutyTariff || 0;
    const rate = fob > 0 ? dt / fob : 0;
    const discount = fob - declared;
    return {
      month: (sh.last_recv || '').slice(0, 7) || '—',
      customer: (sh.customers || [])[0] || '(unknown)',
      division: (sh.divisions || [])[0] || '(unknown)',
      fob, declared, dt, rate, discount, saving: discount * rate,
      tariffUnder: (sh.actual.tariff || 0) - (sh.system.tariff || 0),
      units: sh.units || 0,
    };
  });
  const sumBy = (rows, keyName) => {
    const m = new Map();
    for (const r of rows) {
      const k = r[keyName];
      if (!m.has(k)) m.set(k, { key: k, saving: 0, discount: 0, fob: 0, declared: 0, dt: 0, tariffUnder: 0, ships: 0, units: 0 });
      const a = m.get(k);
      a.saving += r.saving; a.discount += r.discount; a.fob += r.fob; a.declared += r.declared;
      a.dt += r.dt; a.tariffUnder += r.tariffUnder; a.ships += 1; a.units += r.units;
    }
    return [...m.values()].map((a) => ({ ...a, rate: a.fob ? a.dt / a.fob : 0, discPct: a.fob ? (a.discount / a.fob) * 100 : 0 })).sort((x, y) => y.saving - x.saving);
  };
  const byCustomer = sumBy(fsRows, 'customer');
  const byDivision = sumBy(fsRows, 'division');
  const anaTotals = fsRows.reduce((t, r) => ({
    ships: t.ships + 1, fsShips: t.fsShips + (r.discount > 0.5 ? 1 : 0),
    fob: t.fob + r.fob, declared: t.declared + r.declared, dt: t.dt + r.dt,
    discount: t.discount + r.discount, saving: t.saving + r.saving, tariffUnder: t.tariffUnder + Math.max(0, r.tariffUnder),
  }), { ships: 0, fsShips: 0, fob: 0, declared: 0, dt: 0, discount: 0, saving: 0, tariffUnder: 0 });
  anaTotals.rate = anaTotals.fob ? anaTotals.dt / anaTotals.fob : 0;
  // Monthly stacked series — one row per received month, one numeric field per stack key (division or customer).
  const anaMonths = [...new Set(fsRows.map((r) => r.month))].sort();
  const anaStackKeys = [...new Set(fsRows.map((r) => r[stackDim]))].sort((a, b) => {
    const sa = fsRows.filter((r) => r[stackDim] === a).reduce((t, r) => t + r.saving, 0);
    const sb = fsRows.filter((r) => r[stackDim] === b).reduce((t, r) => t + r.saving, 0);
    return sb - sa;
  });
  const anaMonthly = anaMonths.map((mo) => {
    const row = { month: mo, _total: 0 };
    anaStackKeys.forEach((k) => { row[k] = 0; });
    fsRows.filter((r) => r.month === mo).forEach((r) => { row[r[stackDim]] += r.saving; row._total += r.saving; });
    anaStackKeys.forEach((k) => { row[k] = Math.round(row[k]); });
    row._total = Math.round(row._total);
    return row;
  });

  const stySummary = styleData?.summary;
  const styles = styleData?.styles || [];
  const dq = stySummary?.dataQuality || { clean: { styles: 0, duty: 0, tariff: 0, sysDuty: 0 }, excluded: 0, buckets: {} };
  const styUnlinked = styleData?.unlinkedInvoices || []; // by-style view's own unlinked (no job# link)
  const excludedTotal = (dq.excluded || 0) + styUnlinked.length; // exclusions incl. no-job#-link invoices
  const cleanStyles = cleanOnly ? styles.filter((x) => x.clean) : styles;
  // CHA-350 — same search box filters the by-style table
  const shownStyles = cleanStyles.filter((r) => matchesSearch(search, [r.job_number, r.comm_inv, r.sty, r.prod_nums, r.container, r.hts]));

  return (
    <div className="space-y-4">
      {/* View toggle: by shipment (CHA-323) vs by style allocation (CHA-316 phase 2) */}
      <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-sm">
        {[['shipment', 'By shipment'], ['style', 'By style'], ['analysis', 'Analysis']].map(([v, label]) => (
          <button key={v} onClick={() => setView(v)}
            className={`px-3 py-1.5 font-medium ${view === v ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] text-slate-500">Coast</label>
          <select value={coast} onChange={(e) => setCoast(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">All</option><option value="west">West</option><option value="east">East</option>
          </select>
        </div>
        <div>
          {/* CHA-354 — the range selects shipments by LAST receipt (whole-invoice sums) */}
          <label className="block text-[11px] text-slate-500">Last received from</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500">to</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" />
        </div>
        {view === 'shipment' && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 mb-1.5">
            <input type="checkbox" checked={hidePartial} onChange={(e) => setHidePartial(e.target.checked)} /> Hide partial receipts
          </label>
        )}
        {view !== 'analysis' && (
          <div>
            <label htmlFor="ac2-search" className="block text-[11px] text-slate-500">Search</label>
            <input
              id="ac2-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="job# / invoice / PO / style / container…"
              className="w-64 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
        )}
        <button onClick={run} disabled={loading} className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium px-3 py-1.5 rounded">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {loading ? 'Loading…' : 'Run'}
        </button>
        <button onClick={() => (view === 'style' ? exportStyles(styles) : exportCsv(shipments))} disabled={view === 'style' ? !styles.length : !shipments.length} className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-3 py-1.5 rounded">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}
      {loading && (view === 'style' ? !styleData : !data) && <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Reconciling…</div>}

      {view === 'shipment' && s && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card label="Shipments" value={s.shipments} sub={`${s.withActual} w/ ACE · ${s.withFreight} w/ freight`} />
            <Card label="FOB → declared (ACE)" value={`${money(totalFob)} → ${money(totalDeclared)}`} sub={`${pctFmt(pctDiff(totalDeclared, totalFob))} diff`} />
            <Card label="Duty — sys → actual" value={`${money(s.system.duty)} → ${money(s.actual.duty)}`} sub={signed(s.actual.duty - s.system.duty)} />
            <Card label="Tariff — sys → actual" value={`${money(s.system.tariff)} → ${money(s.actual.tariff)}`} sub={signed(s.actual.tariff - s.system.tariff)} />
            <Card label="Freight — sys → actual" value={`${money(s.system.freight)} → ${money(s.actual.freight)}`} sub={signed(s.actual.freight - s.system.freight)} />
            <Card label="Missing data" value={`${missingCount} / ${totalInvoices}`} sub={`${unlinked.length} no job# · ${noActual.length} no ACE`} tone={missingCount ? 'warn' : undefined} />
          </div>

          {/* Verdict legend + missing toggle */}
          <div className="flex flex-wrap items-center gap-3">
            {Object.entries(s.verdicts || {}).map(([v, count]) => (
              <span key={v} className="inline-flex items-center gap-1"><VerdictBadge v={v} /><span className="text-xs text-slate-500">{count}</span></span>
            ))}
            {missingCount > 0 && (
              <button onClick={() => setShowMissing((m) => !m)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800">
                <Search className="h-4 w-4" /> {showMissing ? 'Hide' : 'Show'} what’s missing ({missingCount})
              </button>
            )}
          </div>

          {/* Missing-data panel */}
          {showMissing && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-amber-900">
                  What’s still missing in this selection
                  {/* CHA-379 — dismissed invoices are out of this list; keep the dismissal visible */}
                  {(data?.ignoredInvoices?.length || 0) > 0 && (
                    <span className="ml-2 font-normal text-xs text-slate-500">({data.ignoredInvoices.length} ignored — manage in the Links tab)</span>
                  )}
                </div>
                {unlinked.length > 0 && (
                  <button onClick={() => exportMissing(unlinked.filter((u) => !ignoredLocal[u.comm_inv]))} className="inline-flex items-center gap-1.5 border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 text-xs font-medium px-2.5 py-1 rounded">
                    <Download className="h-3.5 w-3.5" /> Export missing (for broker)
                  </button>
                )}
              </div>
              {shownUnlinked.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">No broker job# yet ({shownUnlinked.length}) — enter the job# if you know it (saves to the lookup; re-run to refresh):</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="text-slate-500"><tr>{['Commercial invoice', 'Terms', 'WH', 'Received', 'Units', 'Set job#', 'Or ignore'].map((h) => <th key={h} className="text-left font-medium px-2 py-1">{h}</th>)}</tr></thead>
                      <tbody className="divide-y divide-amber-100">
                        {shownUnlinked.map((u) => (
                          <tr key={u.comm_inv}>
                            <td className="px-2 py-1 font-mono">{u.comm_inv}</td>
                            <td className="px-2 py-1">{(u.terms || []).join(' ')}</td>
                            <td className="px-2 py-1">{(u.warehouses || []).join(' ')}</td>
                            <td className="px-2 py-1">{u.last_recv || ''}</td>
                            <td className="px-2 py-1 tabular-nums">{u.units}</td>
                            <td className="px-2 py-1">
                              {resolved[u.comm_inv] ? (
                                <span className="inline-flex items-center gap-1 text-green-700"><Check className="h-3.5 w-3.5" />{resolved[u.comm_inv]}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  <input
                                    value={jobInputs[u.comm_inv] || ''}
                                    onChange={(e) => setJobInputs((m) => ({ ...m, [u.comm_inv]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveLink(u.comm_inv); }}
                                    placeholder="B00…"
                                    className="w-24 border border-slate-300 rounded px-1.5 py-0.5 font-mono uppercase focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  />
                                  <button onClick={() => saveLink(u.comm_inv)} disabled={savingInv === u.comm_inv || !(jobInputs[u.comm_inv] || '').trim()} className="text-teal-700 hover:text-teal-800 disabled:opacity-40 font-medium">
                                    {savingInv === u.comm_inv ? '…' : 'Save'}
                                  </button>
                                </span>
                              )}
                            </td>
                            {/* CHA-379 — dismiss instead of link (small/prepaid/parcel); optimistic
                                like `resolved`, the row leaves the server list on the next Run */}
                            <td className="px-2 py-1">
                              {resolved[u.comm_inv] ? (
                                <span className="text-slate-400">—</span>
                              ) : ignoredLocal[u.comm_inv] ? (
                                <span className="text-slate-500">ignored ({ignoredLocal[u.comm_inv]})</span>
                              ) : (
                                <IgnoreControl inv={u.comm_inv} reasons={ignoreReasons || []} tenantId={tenantId}
                                  onDone={(inv, reason) => setIgnoredLocal((m) => ({ ...m, [inv]: reason }))}
                                  onError={setError} />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {search.trim() && !shownUnlinked.length && !shownNoActual.length && (unlinked.length || noActual.length) > 0 && (
                <div className="text-xs text-slate-500">No missing-data rows match the search.</div>
              )}
              {shownNoActual.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">Linked but no ACE duty yet ({shownNoActual.length}) — awaiting the ACE entry:</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="text-slate-500"><tr>{['Shipment (job#)', 'Invoices', 'Terms', 'Received'].map((h) => <th key={h} className="text-left font-medium px-2 py-1">{h}</th>)}</tr></thead>
                      <tbody className="divide-y divide-amber-100">
                        {shownNoActual.map((r) => (
                          <tr key={r.job_number}><td className="px-2 py-1 font-mono">{r.job_number}</td><td className="px-2 py-1">{(r.invoices || []).join(', ')}</td><td className="px-2 py-1">{(r.terms || []).join(' ')}</td><td className="px-2 py-1">{r.last_recv || ''}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shipment table */}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {['Shipment (job#)', 'Inv', 'Terms', 'WH', 'LC', 'Factory', 'Division', 'Received', 'Verdict', 'FOB sys', 'Declared', 'FOB Δ%', 'Duty sys', 'Duty act', 'Δ duty', 'Tariff sys', 'Tariff act', 'Δ tariff', 'D+T sys', 'D+T act', 'Save %', 'Freight sys', 'Freight act', 'Δ freight'].map((h) => (
                    <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {/* Comparison table = shipments WITH an ACE actual only; no-ACE shipments live in the
                    "No ACE yet" missing panel above, not here (nothing to compare). */}
                {shownPosted.map((r) => {
                  const diffCls = (v) => (v == null ? 'text-slate-400' : v < 0 ? 'text-green-700' : 'text-red-700');
                  return (
                    <tr key={r.job_number} className="hover:bg-slate-50">
                      <td className="px-3 py-1.5 font-mono text-xs">{r.job_number}</td>
                      <td className="px-3 py-1.5">{r.invoiceCount}</td>
                      <td className="px-3 py-1.5 text-xs">{(r.terms || []).join(' ') || '—'}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-500">{(r.warehouses || []).join(', ')}</td>
                      {/* CHA-422 — LC / factory resolve ERP codes to company names; an unresolved code
                          shows AS the code, never blank, so "name missing from the master" cannot be
                          misread as "no LC". Multi-valued because a job can span several invoices. */}
                      <td className="px-3 py-1.5 text-xs" title={(r.lc_names || []).join(', ')}>{(r.lc_names || []).join(', ') || '—'}</td>
                      <td className="px-3 py-1.5 text-xs" title={(r.factory_names || []).join(', ')}>{(r.factory_names || []).join(', ') || '—'}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-500">{(r.division_names || []).join(', ') || (r.divisions || []).join(', ') || '—'}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-500 whitespace-nowrap">{r.last_recv || '—'}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <VerdictBadge v={r.verdict} />
                        {r.hasRollIn && <span title="This shipment has an HTS whose OLD tariff is rolled into the ERP duty rate — the broker books it as tariff instead, so read Duty + Tariff COMBINED (the D+T columns), not each column." className="ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-800 cursor-help">Old tariff in duty</span>}
                        {/* CHA-355 — name the never-received siblings so the operator can chase the receipts (or fix the link spelling) */}
                        {r.partialReceipts && <span title={`ACE covers invoice(s) with no received counterpart: ${(r.missingInvoices || []).join(', ')}`} className="ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 cursor-help">missing: {(r.missingInvoices || []).join(', ').slice(0, 40)}{((r.missingInvoices || []).join(', ').length > 40) ? '…' : ''}</span>}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.system.fob)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.actual.value)}</td>
                      <td className="px-3 py-1.5 tabular-nums font-medium">{pctFmt(pctDiff(r.actual.value, r.system.fob))}</td>
                      <td className="px-3 py-1.5 tabular-nums">{money(r.system.duty)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{money(r.actual.duty)}</td>
                      <td className={`px-3 py-1.5 tabular-nums font-medium ${diffCls(r.variance.duty)}`}>{signed(r.variance.duty)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.system.tariff)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.actual.tariff)}</td>
                      <td className={`px-3 py-1.5 tabular-nums ${diffCls(r.variance.tariff)}`}>{signed(r.variance.tariff)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-700 font-medium">{money(r.system.dutyTariff)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-700 font-medium">{money(r.actual.dutyTariff)}</td>
                      <td className="px-3 py-1.5 tabular-nums font-medium" title="Combined duty+tariff saving vs system — compare to FOB Δ%; on a clean first sale they should roughly match">{r.savingPct == null ? '—' : `${r.savingPct > 0 ? '' : ''}${r.savingPct.toFixed(1)}%`}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.system.freight)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.actual.freight)}</td>
                      <td className={`px-3 py-1.5 tabular-nums ${diffCls(r.variance.freight)}`}>{signed(r.variance.freight)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!shownPosted.length && (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2 p-4"><DollarSign className="h-4 w-4" />{posted.length ? 'No shipments match the search.' : shipments.length ? 'No shipments with an ACE actual yet — see “No ACE yet” above.' : 'No in-scope shipments for this selection.'}</div>
          )}
        </>
      )}

      {view === 'style' && stySummary && (
        <>
          {/* Summary — style-grain allocation. CLEAN = trustworthy actual-cost roll-up; excluded = needs a data fix. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card label="Clean allocation" value={`${dq.clean.styles} / ${stySummary.styles}`} sub={`${money(dq.clean.duty)} duty · ${money(dq.clean.tariff)} tariff`} tone={dq.excluded ? 'warn' : undefined} />
            <Card label="Duty — sys → actual (clean)" value={`${money(dq.clean.sysDuty)} → ${money(dq.clean.duty)}`} sub={signed(dq.clean.duty - dq.clean.sysDuty)} />
            <Card label="Excluded — needs data fix" value={excludedTotal} sub={`${dq.excluded} styles · ${styUnlinked.length} no job#`} tone={excludedTotal ? 'warn' : undefined} />
            <Card label="Style lines" value={stySummary.styles} sub={`${stySummary.jobs} shipments`} />
            <Card label="Tariff — sys → actual (all)" value={`${money(stySummary.system.tariff)} → ${money(stySummary.actual.tariff)}`} sub={signed(stySummary.actual.tariff - stySummary.system.tariff)} />
            <Card label="Freight — sys → actual" value={`${money(stySummary.system.freight)} → ${money(stySummary.actual.freight)}`} sub={`${signed(stySummary.actual.freight - stySummary.system.freight)} · ${stySummary.withFreight || 0} w/ freight`} />
          </div>

          {/* Verdict legend + data-quality / clean-only controls */}
          <div className="flex flex-wrap items-center gap-3">
            {Object.entries(stySummary.verdicts || {}).map(([v, count]) => (
              <span key={v} className="inline-flex items-center gap-1"><VerdictBadge v={v} /><span className="text-xs text-slate-500">{count}</span></span>
            ))}
            <label className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={cleanOnly} onChange={(e) => setCleanOnly(e.target.checked)} /> Clean only
            </label>
            {excludedTotal > 0 && (
              <button onClick={() => setShowDQ((m) => !m)} className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800">
                <Search className="h-4 w-4" /> {showDQ ? 'Hide' : 'Data quality'} ({excludedTotal})
              </button>
            )}
          </div>

          {/* Data-quality panel — each bucket is a data fix (lookup table vs source), exportable to work the list */}
          {showDQ && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-2">
              <div className="text-sm font-semibold text-amber-900">Excluded from the clean roll-up — fix the data, then re-run</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-slate-500"><tr>{['Bucket', 'Styles', 'Units', 'Sys duty exposure', 'Fix', ''].map((h) => <th key={h} className="text-left font-medium px-2 py-1">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-amber-100">
                    {[
                      { key: 'noJobLink', label: 'No broker job# link', count: styUnlinked.length, units: styUnlinked.reduce((t, u) => t + (u.units || 0), 0), sys: null, fix: 'Lookup table — add commercial invoice → job#', exp: () => exportMissing(styUnlinked) },
                      { key: 'noAce', label: 'Shipment not in ACE', b: dq.buckets.noAce, fix: 'Source — ACE upload / timing', reason: 'no-ace' },
                      { key: 'htsNoMatch', label: 'HTS not matched in ACE', b: dq.buckets.htsNoMatch, fix: 'Source — HTS classification', reason: 'hts-no-match' },
                      { key: 'missingCost', label: 'Missing component cost', b: dq.buckets.missingCost, fix: 'Source — hts_updates cost (CHA-329)', reason: 'missing-cost' },
                      { key: 'partial', label: 'Partial HTS match', b: dq.buckets.partial, fix: 'Source — HTS / partial entry', reason: 'partial' },
                    ].filter((r) => (r.b ? r.b.styles : r.count) > 0).map((r) => (
                      <tr key={r.key}>
                        <td className="px-2 py-1 font-medium text-slate-700">{r.label}</td>
                        <td className="px-2 py-1 tabular-nums">{r.b ? r.b.styles : r.count}</td>
                        <td className="px-2 py-1 tabular-nums">{Math.round(r.b ? r.b.units : r.units).toLocaleString()}</td>
                        <td className="px-2 py-1 tabular-nums">{r.b ? money(r.b.sysDuty) : '—'}</td>
                        <td className="px-2 py-1 text-slate-600">{r.fix}</td>
                        <td className="px-2 py-1">
                          <button onClick={r.exp || (() => exportStyles(styles.filter((s) => s.excludeReason === r.reason)))} className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-800 font-medium">
                            <Download className="h-3.5 w-3.5" /> Export
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Style table */}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {['Shipment (job#)', 'Commercial inv', 'Style', 'HTS', 'Terms', 'Verdict', 'Units', 'FOB sys', 'Duty sys', 'Duty act', 'Δ duty', 'Tariff sys', 'Tariff act', 'Δ tariff', 'Freight sys', 'Freight act', 'Δ freight'].map((h) => (
                    <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shownStyles.map((r, i) => {
                  const diffCls = (v) => (v == null ? 'text-slate-400' : v < 0 ? 'text-green-700' : 'text-red-700');
                  return (
                    <tr key={`${r.job_number}|${r.comm_inv}|${r.sty}|${r.hts}|${i}`} className={`hover:bg-slate-50 ${r.unallocatedDuty ? 'bg-orange-50/40' : (r.approxDuty || r.partialDuty) ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.job_number}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.comm_inv || '—'}</td>
                      <td className="px-3 py-1.5 text-xs">{r.sty || '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{r.hts || '—'}</td>
                      <td className="px-3 py-1.5 text-xs">{(r.terms || []).join(' ') || '—'}</td>
                      <td className="px-3 py-1.5"><VerdictBadge v={r.verdict} /></td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{Math.round(r.units).toLocaleString()}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.system.fob)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{money(r.system.duty)}</td>
                      <td className="px-3 py-1.5 tabular-nums">
                        {r.approxDuty && <span title="Approximate — a component in this (job, HTS) group has no cost in hts_updates; back-fill pending (CHA-329)" className="text-amber-600 cursor-help mr-0.5">~</span>}
                        {r.partialDuty && <span title="Partial — some of this style's HTS components matched no ACE line" className="text-amber-600 cursor-help mr-0.5">◐</span>}
                        {money(r.actual.duty)}
                      </td>
                      <td className={`px-3 py-1.5 tabular-nums font-medium ${diffCls(r.variance.duty)}`}>{signed(r.variance.duty)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.system.tariff)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.actual.tariff)}</td>
                      <td className={`px-3 py-1.5 tabular-nums ${diffCls(r.variance.tariff)}`}>{signed(r.variance.tariff)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.system.freight)}</td>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(r.actual.freight)}</td>
                      <td className={`px-3 py-1.5 tabular-nums ${diffCls(r.variance.freight)}`}>{signed(r.variance.freight)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!shownStyles.length && (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2 p-4"><DollarSign className="h-4 w-4" />{search.trim() && cleanStyles.length ? 'No style lines match the search.' : cleanOnly && styles.length ? 'No clean style lines (all excluded — see Data quality).' : 'No in-scope style lines for this selection.'}</div>
          )}
        </>
      )}

      {view === 'analysis' && s && (
        posted.length ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="First-sale saving (duty + tariff)" value={money(anaTotals.saving)} sub={`FOB lowered by ${money(anaTotals.discount)} × ${(anaTotals.rate * 100).toFixed(1)}% rate`} />
            <Card label="FOB discount" value={anaTotals.fob ? `${((anaTotals.discount / anaTotals.fob) * 100).toFixed(1)}%` : '—'} sub={`${money(anaTotals.fob)} FOB → ${money(anaTotals.declared)} declared`} />
            <Card label="First-sale shipments" value={anaTotals.fsShips.toLocaleString()} sub={`of ${anaTotals.ships.toLocaleString()} ACE-matched (FOB was lowered)`} />
            <Card label="Tariff under-accrual" value={money(anaTotals.tariffUnder)} sub="actual tariff over ERP system (301/IEEPA gap)" tone={anaTotals.tariffUnder ? 'warn' : undefined} />
          </div>
          <div className="text-xs text-slate-500">First-sale saving = <span className="font-medium">(original FOB − first-sale declared value) × duty+tariff rate</span>. Example: FOB $100 declared at $90 with a 10% rate saves $1. This isolates the first-sale benefit (it does not depend on how the ERP vs broker split duty/tariff).</div>

          {/* Monthly first-sale savings — stacked by division or customer */}
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-700">First-sale saving by month</div>
              <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
                {[['division', 'Division'], ['customer', 'Customer']].map(([v, label]) => (
                  <button key={v} onClick={() => setStackDim(v)} className={`px-2.5 py-1 ${stackDim === v ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>Stack by {label}</button>
                ))}
              </div>
            </div>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={anaMonthly} margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => money(v)} tick={{ fontSize: 11 }} width={64} />
                  <Tooltip formatter={(v, n) => [money(v), n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {anaStackKeys.map((k, i) => (
                    <Bar key={k} dataKey={k} stackId="s" fill={ANA_PALETTE[i % ANA_PALETTE.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <AnaBreakdown title="First-sale saving by customer" rows={byCustomer} keyLabel="Customer" />
          <AnaBreakdown title="First-sale saving by division" rows={byDivision} keyLabel="Division" />

          <div className="text-xs text-slate-500">Savings shown are duty + tariff. Freight savings from first sale are not modeled here (freight is volume-based, unaffected by declared value).</div>
        </>
        ) : (
          <div className="text-sm text-slate-500 inline-flex items-center gap-2 p-4"><DollarSign className="h-4 w-4" />No ACE-matched shipments for this selection.</div>
        )
      )}
    </div>
  );
}
